import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { 
  IoDocumentTextOutline,
  IoPencilOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoTrophyOutline,
  IoTrendingUpOutline,
  IoChatbubblesOutline,
  IoRocketOutline
} from 'react-icons/io5';
import { Achievement } from '../../../hooks/useAchievements';

interface ReviewDocumentCreatorProps {
  focusId: string;
  preparationData: {
    selectedAchievements: Achievement[];
    keyMetrics: any[];
    talkingPoints: {
      accomplishments: string[];
      challenges: string[];
      growth: string[];
      futureGoals: string[];
    };
    reviewSummary: string;
  };
  onSave: (reviewData: any) => void;
  isSaving: boolean;
}

const ReviewDocumentCreator: React.FC<ReviewDocumentCreatorProps> = ({
  focusId,
  preparationData,
  onSave,
  isSaving
}) => {
  const { darkMode } = useDarkMode();
  const [reviewTitle, setReviewTitle] = useState(
    `Performance Review ${new Date().getFullYear()}`
  );
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);

  // Style classes
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-700' : 'bg-white';
  const sectionBg = darkMode ? 'bg-gray-900' : 'bg-gray-50';

  // Generate review content
  const generateReviewContent = () => {
    const sections = [];

    // Executive Summary
    if (executiveSummary) {
      sections.push(`## Executive Summary\n\n${executiveSummary}\n`);
    }

    // Key Metrics
    if (preparationData.keyMetrics.length > 0) {
      sections.push(`## Key Performance Metrics\n`);
      preparationData.keyMetrics.forEach(metric => {
        const improvement = metric.improvement ? ` (${metric.improvement > 0 ? '+' : ''}${metric.improvement}% from baseline)` : '';
        sections.push(`- **${metric.metric_name}**: ${metric.metric_value} ${metric.metric_unit || ''}${improvement}`);
      });
      sections.push('');
    }

    // Achievements
    if (preparationData.selectedAchievements.length > 0) {
      sections.push(`## Major Achievements\n`);
      preparationData.selectedAchievements.forEach((achievement, index) => {
        sections.push(`### ${index + 1}. ${achievement.title}\n`);
        sections.push(`**Situation**: ${achievement.situation}\n`);
        sections.push(`**Task**: ${achievement.task}\n`);
        sections.push(`**Action**: ${achievement.action}\n`);
        sections.push(`**Result**: ${achievement.result}\n`);
        if (achievement.key_skills && achievement.key_skills.length > 0) {
          sections.push(`**Skills Demonstrated**: ${achievement.key_skills.join(', ')}\n`);
        }
      });
    }

    // Talking Points
    const talkingPointSections = [
      { key: 'accomplishments', title: 'Key Accomplishments' },
      { key: 'challenges', title: 'Challenges Overcome' },
      { key: 'growth', title: 'Growth & Development' },
      { key: 'futureGoals', title: 'Future Goals' }
    ];

    talkingPointSections.forEach(section => {
      const points = preparationData.talkingPoints[section.key as keyof typeof preparationData.talkingPoints];
      if (points && points.length > 0) {
        sections.push(`## ${section.title}\n`);
        points.forEach(point => {
          sections.push(`- ${point}`);
        });
        sections.push('');
      }
    });

    return sections.join('\n');
  };

  const handleSave = () => {
    const reviewData = {
      title: reviewTitle,
      executiveSummary,
      content: generateReviewContent(),
      metadata: {
        achievementCount: preparationData.selectedAchievements.length,
        metricCount: preparationData.keyMetrics.length,
        focusId,
        createdAt: new Date().toISOString()
      }
    };

    onSave(reviewData);
  };

  const getAchievementIcon = (category?: string) => {
    const icons: Record<string, string> = {
      technical: '💻',
      leadership: '👥',
      collaboration: '🤝',
      innovation: '💡',
      impact: '🎯',
      growth: '📈'
    };
    return icons[category || ''] || '🏆';
  };

  return (
    <div className="space-y-6">
      {/* Review Title */}
      <div className={`p-6 rounded-lg ${cardBg} border ${borderColor}`}>
        <div className="flex items-center justify-between mb-2">
          <label className={`text-sm font-medium ${mutedTextColor}`}>
            Review Title
          </label>
          {!isEditingTitle && (
            <button
              onClick={() => setIsEditingTitle(true)}
              className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <IoPencilOutline className="w-4 h-4" />
            </button>
          )}
        </div>
        {isEditingTitle ? (
          <div>
            <input
              type="text"
              value={reviewTitle}
              onChange={(e) => setReviewTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && setIsEditingTitle(false)}
              className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              autoFocus
            />
            <button
              onClick={() => setIsEditingTitle(false)}
              className="mt-2 text-sm text-purple-600 hover:text-purple-700"
            >
              Done
            </button>
          </div>
        ) : (
          <h2 className={`text-2xl font-bold ${textColor}`}>{reviewTitle}</h2>
        )}
      </div>

      {/* Executive Summary */}
      <div className={`p-6 rounded-lg ${cardBg} border ${borderColor}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-medium ${textColor}`}>
            Executive Summary
          </h3>
          {!isEditingSummary && (
            <button
              onClick={() => setIsEditingSummary(true)}
              className={`px-3 py-1 text-sm rounded-lg border ${borderColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              {executiveSummary ? 'Edit' : 'Add Summary'}
            </button>
          )}
        </div>
        {isEditingSummary ? (
          <div>
            <textarea
              value={executiveSummary}
              onChange={(e) => setExecutiveSummary(e.target.value)}
              placeholder="Write a brief summary of your performance highlights..."
              rows={4}
              className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              autoFocus
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setIsEditingSummary(false)}
                className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setExecutiveSummary('');
                  setIsEditingSummary(false);
                }}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className={`${executiveSummary ? textColor : mutedTextColor}`}>
            {executiveSummary || 'Click "Add Summary" to write an executive summary for your review.'}
          </p>
        )}
      </div>

      {/* Preview Sections */}
      <div className="space-y-4">
        <h3 className={`text-lg font-medium ${textColor}`}>Review Preview</h3>

        {/* Metrics Summary */}
        {preparationData.keyMetrics.length > 0 && (
          <div className={`p-4 rounded-lg ${sectionBg} border ${borderColor}`}>
            <h4 className={`font-medium ${textColor} mb-3 flex items-center gap-2`}>
              <IoTrendingUpOutline className="w-5 h-5" />
              Key Performance Metrics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {preparationData.keyMetrics.slice(0, 4).map((metric, index) => (
                <div key={index} className={`flex justify-between p-2 rounded ${cardBg}`}>
                  <span className={`text-sm ${mutedTextColor}`}>{metric.metric_name}</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    {metric.metric_value} {metric.metric_unit}
                    {metric.improvement && (
                      <span className={`ml-2 text-xs ${
                        parseFloat(metric.improvement) > 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {parseFloat(metric.improvement) > 0 ? '+' : ''}{metric.improvement}%
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Achievements */}
        {preparationData.selectedAchievements.length > 0 && (
          <div className={`p-4 rounded-lg ${sectionBg} border ${borderColor}`}>
            <h4 className={`font-medium ${textColor} mb-3 flex items-center gap-2`}>
              <IoTrophyOutline className="w-5 h-5" />
              Featured Achievements ({preparationData.selectedAchievements.length})
            </h4>
            <div className="space-y-2">
              {preparationData.selectedAchievements.map((achievement, index) => (
                <div key={index} className={`flex items-start gap-3 p-2 rounded ${cardBg}`}>
                  <span className="text-xl">{getAchievementIcon(achievement.category)}</span>
                  <div className="flex-1">
                    <h5 className={`font-medium ${textColor}`}>{achievement.title}</h5>
                    <p className={`text-sm ${mutedTextColor} line-clamp-1`}>
                      {achievement.result}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Talking Points Summary */}
        <div className={`p-4 rounded-lg ${sectionBg} border ${borderColor}`}>
          <h4 className={`font-medium ${textColor} mb-3 flex items-center gap-2`}>
            <IoChatbubblesOutline className="w-5 h-5" />
            Talking Points Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'accomplishments', label: 'Accomplishments', icon: '🎯' },
              { key: 'challenges', label: 'Challenges', icon: '💪' },
              { key: 'growth', label: 'Growth', icon: '📈' },
              { key: 'futureGoals', label: 'Goals', icon: '🚀' }
            ].map(section => (
              <div key={section.key} className={`text-center p-3 rounded ${cardBg}`}>
                <div className="text-2xl mb-1">{section.icon}</div>
                <div className={`text-sm ${mutedTextColor}`}>{section.label}</div>
                <div className={`text-lg font-bold ${textColor}`}>
                  {preparationData.talkingPoints[section.key as keyof typeof preparationData.talkingPoints].length}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className={`p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800`}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className={`font-medium text-green-800 dark:text-green-200`}>
              Ready to Create Your Review Document?
            </h4>
            <p className={`text-sm text-green-700 dark:text-green-300 mt-1`}>
              This will save your review preparation and create a shareable document.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors ${
              isSaving ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <IoCheckmarkCircleOutline className="w-5 h-5" />
                <span>Create Review</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewDocumentCreator;